// «Помощь» (030: T019; FR-032; contracts/api.md, «Учётная запись», auth.help; ASG-ONB-15): вопрос
// ответственного контролёру. Окно — форма из одного поля «Вопрос» (1–2000 знаков); к вопросу
// добавляются название экрана и пункт, если вопрос о нём, — окно называет их, чтобы человек знал,
// что уйдёт. Отправлено — окно закрывается, тост «Вопрос отправлен контролёру». Проверка — при
// отправке (ux.md «Табеля», раздел 13): пусто или длиннее 2000 — текст под полем, фокус в поле,
// введённое не стирается. Отказ сервера по полю — под полем и выноской с кодом (T058), другой —
// выноской с причиной, кодом и номером операции; введённое остаётся. До 640 px окно выезжает
// снизу (Modal). Кнопка — «Отправить вопрос»: действие и объект («Табель», «Тексты», MUST; T044).
import { HELP_SCREEN_MAX, HELP_TEXT_MAX, fieldMessages } from '@kp/contracts';
import { Button, Callout, Modal, ModalFooter, TextAreaField, useToast } from '@kp/ui';
import { useId, useRef, useState } from 'react';
import type { FormEvent, RefObject } from 'react';
import { api, isApiError } from '../../api/client.ts';
import { FieldsRefusalCallout } from '../../app/FieldsRefusal.tsx';
import { errorView } from '../../app/errors.ts';
import type { ErrorView, FieldsRefusal } from '../../app/errors.ts';
import './help.css';

/** Ошибка поля «Вопрос» при отправке: пусто или длиннее предела. */
export function helpTextError(value: string): string | undefined {
  const text = value.trim();
  return text === '' || text.length > HELP_TEXT_MAX ? fieldMessages.helpText : undefined;
}

export interface HelpDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Название экрана, откуда вопрос: «Мои задачи». */
  screen: string;
  /** Пункт, если вопрос о нём. */
  itemId?: string;
  /** Подпись пункта в окне: «3.2 · П-12». */
  itemLabel?: string;
  returnFocusRef?: RefObject<HTMLElement | null>;
}

export function HelpDialog({
  open,
  onOpenChange,
  screen,
  itemId,
  itemLabel,
  returnFocusRef,
}: HelpDialogProps) {
  const toast = useToast();
  const formId = useId();
  const [text, setText] = useState('');
  const [error, setError] = useState<string | undefined>();
  const [failure, setFailure] = useState<ErrorView | null>(null);
  const [refusal, setRefusal] = useState<FieldsRefusal<'text'> | null>(null);
  const [pending, setPending] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const screenName = screen.trim().slice(0, HELP_SCREEN_MAX);

  const showError = (message: string) => {
    setError(message);
    inputRef.current?.focus();
  };

  const submit = async (event?: FormEvent) => {
    event?.preventDefault();
    if (pending) return;
    setRefusal(null);
    const found = helpTextError(text);
    if (found) {
      showError(found);
      return;
    }
    setPending(true);
    setFailure(null);
    try {
      await api('auth.help', {
        body: { text: text.trim(), screen: screenName, ...(itemId ? { itemId } : {}) },
      });
    } catch (caught) {
      setPending(false);
      const fieldText = isApiError(caught, 'REC-007') ? caught.fields.text : undefined;
      if (fieldText) {
        showError(fieldText);
        setRefusal({ fields: { text: fieldText }, view: errorView(caught) });
        return;
      }
      setFailure(errorView(caught));
      return;
    }
    setPending(false);
    onOpenChange(false);
    toast.ok(
      itemLabel ? 'Вопрос отправлен владельцу протокола' : 'Вопрос отправлен администраторам',
    );
  };

  const context = itemLabel
    ? `К вопросу добавим экран «${screenName}» и пункт ${itemLabel}.`
    : `К вопросу добавим экран «${screenName}».`;

  return (
    <Modal
      open={open}
      onOpenChange={(next) => (pending ? undefined : onOpenChange(next))}
      title="Помощь"
      description={
        itemLabel ? 'Вопрос получит владелец протокола.' : 'Вопрос получат администраторы.'
      }
      returnFocusRef={returnFocusRef}
      onSubmitShortcut={() => void submit()}
      footer={
        <ModalFooter
          submitHint
          primary={
            <Button type="submit" form={formId} variant="primary" loading={pending}>
              Отправить вопрос
            </Button>
          }
        />
      }
    >
      <form id={formId} className="t-col" noValidate onSubmit={(event) => void submit(event)}>
        {failure ? (
          <Callout
            tone="bad"
            role="alert"
            lead={`${failure.message}.`}
            code={failure.code}
            operation={failure.operation}
          >
            {failure.nextStep}
          </Callout>
        ) : null}
        <FieldsRefusalCallout refusal={refusal} errors={{ text: error }} />
        <TextAreaField
          ref={inputRef}
          label="Вопрос"
          required
          rows={5}
          value={text}
          placeholder="например, срок пункта уже перенесли на планёрке"
          hint={`До ${HELP_TEXT_MAX} знаков`}
          error={error}
          onChange={(event) => {
            setText(event.target.value);
            if (error && helpTextError(event.target.value) === undefined) setError(undefined);
          }}
        />
        <p className="kp-help-context">{context}</p>
      </form>
    </Modal>
  );
}
